import React, { useEffect, useState } from "react";
import { Activity, Copy, Link2, Mail, RefreshCw, Unlink, Check, X as XIcon } from "lucide-react";
import {
  athleteStravaStatus,
  createStravaLinkUrl,
  sendStravaInvite,
  setStravaActivityCountsAsVolume,
  syncStravaActivities,
  unlinkStrava,
  listStravaActivities,
} from "../../lib/api";
import { toast } from "sonner";

function formatDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Strava panel for a single coaching athlete.
 * Three states:
 *   1. Strava not configured on server → placeholder, disabled.
 *   2. Athlete not linked → "Generate connect link", copy to clipboard.
 *   3. Linked → shows linked name, sync/unlink, recent activities list.
 */
export default function StravaPanel({ athlete, onChange }) {
  const athleteId = athlete.id;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [activities, setActivities] = useState([]);

  const refreshStatus = async () => {
    setLoading(true);
    try {
      const s = await athleteStravaStatus(athleteId);
      setStatus(s);
      if (s.linked) {
        try {
          const list = await listStravaActivities(athleteId);
          setActivities(list || []);
        } catch {
          /* ignore */
        }
      }
    } catch {
      toast.error("Couldn't load Strava status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  // If the page was redirected to after a linking flow, re-check.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("strava") === "linked") {
      toast.success("Strava linked successfully");
      refreshStatus();
      onChange?.();
      sp.delete("strava");
      const qs = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    } else if (sp.get("strava_error")) {
      toast.error(`Strava link failed: ${sp.get("strava_error")}`);
      sp.delete("strava_error");
      const qs = sp.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGenerateLink = async () => {
    setWorking(true);
    try {
      const { url } = await createStravaLinkUrl(athleteId);
      setLinkUrl(url);
      // Try auto-copy
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Connect link copied to clipboard");
      } catch {
        toast.info("Copy the link below and email it to your athlete");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't create link";
      toast.error(detail);
    } finally {
      setWorking(false);
    }
  };

  const onCopyLink = async () => {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy — select manually");
    }
  };

  const onSendInvite = async () => {
    if (!athlete?.email) {
      toast.error("Add an email on the athlete's profile first.");
      return;
    }
    if (!window.confirm(`Email a Strava connect invite to ${athlete.email}?`)) {
      return;
    }
    setWorking(true);
    try {
      const r = await sendStravaInvite(athleteId);
      toast.success(`Invite emailed to ${r.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send invite");
    } finally {
      setWorking(false);
    }
  };

  const onSync = async () => {
    setWorking(true);
    try {
      const { synced } = await syncStravaActivities(athleteId, 30);
      toast.success(`Synced ${synced} activities`);
      await refreshStatus();
    } catch {
      toast.error("Sync failed");
    } finally {
      setWorking(false);
    }
  };

  const onUnlink = async () => {
    if (!window.confirm("Unlink Strava? Cached activities will be removed.")) return;
    setWorking(true);
    try {
      await unlinkStrava(athleteId);
      toast.success("Strava unlinked");
      setLinkUrl("");
      setActivities([]);
      await refreshStatus();
    } catch {
      toast.error("Unlink failed");
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-[var(--tir-border)] bg-white p-6 text-sm text-[var(--tir-muted)]">
        Loading Strava status…
      </div>
    );
  }

  // Server not configured → placeholder
  if (status && !status.configured) {
    return (
      <div
        className="border border-dashed border-[var(--tir-border)] bg-white p-6"
        data-testid="strava-panel-unconfigured"
      >
        <div className="flex items-start gap-3">
          <Activity size={18} className="text-[var(--tir-muted)] mt-1 shrink-0" />
          <div>
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              Strava integration
            </div>
            <p className="text-sm text-[var(--tir-muted)] mt-1 leading-relaxed">
              Not yet configured on this server. Add <code>STRAVA_CLIENT_ID</code> and{" "}
              <code>STRAVA_CLIENT_SECRET</code> to <code>backend/.env</code> to enable
              athlete Strava linking.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Not linked
  if (!status.linked) {
    return (
      <div
        className="border border-[var(--tir-border)] bg-white p-6"
        data-testid="strava-panel-unlinked"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Activity size={18} className="text-[#fc4c02] mt-1 shrink-0" />
            <div>
              <div className="font-serif-display text-xl text-[var(--tir-ink)]">
                Strava
              </div>
              <p className="text-sm text-[var(--tir-muted)] mt-1 leading-relaxed max-w-xl">
                Email a one-time Strava connect invite to {athlete.name}. Once they
                accept and authorize, their runs flow in automatically and auto-match
                to the prescribed workout on each day.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={onSendInvite}
              disabled={working || !athlete?.email}
              data-testid="strava-email-invite-btn"
              title={athlete?.email ? "Email a one-time Strava connect link to the athlete" : "Add an email on the athlete's profile first"}
              className="inline-flex items-center gap-2 bg-[#fc4c02] text-white px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#e24400] transition-colors disabled:opacity-50"
            >
              <Mail size={14} /> {working ? "Working…" : "Email invite to athlete"}
            </button>
            <button
              type="button"
              onClick={onGenerateLink}
              disabled={working}
              data-testid="generate-strava-link-btn"
              title="Generate a copy-paste link instead"
              className="inline-flex items-center gap-2 bg-white text-[var(--tir-ink)] border border-[var(--tir-border)] px-5 py-2.5 text-xs tracking-[0.2em] uppercase font-semibold hover:border-[var(--tir-ink)] transition-colors disabled:opacity-50"
            >
              <Link2 size={14} /> Copy link
            </button>
          </div>
        </div>
        {linkUrl && (
          <div className="mt-5 border-t border-[var(--tir-border)] pt-4">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
              One-time connect link
            </div>
            <div className="flex gap-2 items-center">
              <input
                readOnly
                value={linkUrl}
                data-testid="strava-link-url"
                className="flex-1 bg-[var(--tir-ivory)] border border-[var(--tir-border)] px-3 py-2 text-xs font-mono-tir text-[var(--tir-ink)] focus:outline-none"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                onClick={onCopyLink}
                className="inline-flex items-center gap-1 border border-[var(--tir-ink)] bg-white px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            <p className="text-[11px] text-[var(--tir-muted)] mt-2 italic">
              Link expires in 30 minutes. Once clicked, it can't be reused.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Linked
  return (
    <div
      className="border border-[var(--tir-border)] bg-white p-6"
      data-testid="strava-panel-linked"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Activity size={18} className="text-[#fc4c02] mt-1 shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-serif-display text-xl text-[var(--tir-ink)]">
                Strava
              </span>
              <span className="text-[10px] tracking-[0.24em] uppercase bg-[#fc4c02]/10 text-[#b33700] px-2 py-0.5 font-semibold">
                Connected
              </span>
            </div>
            <p className="text-sm text-[var(--tir-muted)] mt-1">
              {status.strava_name || `Strava athlete #${status.strava_athlete_id}`} · linked{" "}
              {formatDateShort(status.linked_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSync}
            disabled={working}
            data-testid="sync-strava-btn"
            className="inline-flex items-center gap-1.5 border border-[var(--tir-ink)] bg-white px-3 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} /> Sync
          </button>
          <button
            type="button"
            onClick={onUnlink}
            disabled={working}
            data-testid="unlink-strava-btn"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] px-2 py-2 font-semibold disabled:opacity-50"
          >
            <Unlink size={12} /> Unlink
          </button>
        </div>
      </div>

      {activities.length > 0 && (
        <div className="mt-5 border-t border-[var(--tir-border)] pt-4">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
            Recent activities
          </div>
          <ul className="divide-y divide-[var(--tir-border)]">
            {activities.slice(0, 8).map((a) => {
              const km = (a.distance_m || 0) / 1000;
              const mins = Math.round((a.moving_time_s || 0) / 60);
              const counts = !!a.counts_as_volume;
              const toggle = async () => {
                try {
                  const updated = await setStravaActivityCountsAsVolume(
                    athleteId,
                    a.strava_activity_id,
                    !counts,
                  );
                  setActivities((prev) =>
                    prev.map((p) =>
                      p.strava_activity_id === a.strava_activity_id
                        ? { ...p, counts_as_volume: updated.counts_as_volume }
                        : p,
                    ),
                  );
                  toast.success(
                    updated.counts_as_volume
                      ? "Counts toward weekly volume"
                      : "Excluded from weekly volume",
                  );
                  onChange?.();
                } catch {
                  toast.error("Couldn't update");
                }
              };
              return (
                <li
                  key={a.strava_activity_id}
                  className="py-2 flex items-baseline justify-between gap-3"
                  data-testid={`strava-activity-${a.strava_activity_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[var(--tir-ink)] truncate">{a.name}</div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] font-semibold mt-0.5">
                      {a.sport_type || a.type || "Activity"} ·{" "}
                      {formatDateShort(a.start_date_local)}
                    </div>
                  </div>
                  <div className="font-mono-tir text-xs text-[var(--tir-ink)] whitespace-nowrap">
                    {km > 0 ? `${km.toFixed(1)} km` : ""}
                    {km > 0 && mins > 0 ? " · " : ""}
                    {mins > 0 ? `${mins} min` : ""}
                  </div>
                  <button
                    type="button"
                    onClick={toggle}
                    data-testid={`strava-activity-counts-toggle-${a.strava_activity_id}`}
                    title={
                      counts
                        ? "Currently counts toward weekly volume — click to exclude"
                        : "Excluded from weekly volume — click to include"
                    }
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap border transition-colors ${
                      counts
                        ? "bg-[#fc4c02]/10 text-[#b33700] border-[#fc4c02]/40 hover:bg-[#fc4c02] hover:text-white"
                        : "bg-white text-[var(--tir-muted)] border-[var(--tir-border)] hover:border-[var(--tir-ink)] hover:text-[var(--tir-ink)]"
                    }`}
                  >
                    {counts ? <Check size={10} /> : <XIcon size={10} />}
                    {counts ? "Counts" : "Excluded"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
